import CustomForm from "@components/form/CustomForm";
import PageWrapper from "@components/layouts/PageWrapper";
import { useCallback, useEffect, useMemo, useState } from "react";
import { productPriceFields } from "@helpers/fields/product/product-price.fields";
import { productPriceSchema } from "@helpers/validation-schemas/product/product-price.schema";
import { useGetAllProductCategoriesQuery } from "@states/product/product-category.api";
import { TSuccessResponse } from "@type-defs/general/TSuccessResponse";
import { TProductCategory } from "@type-defs/product/TProductCategory";
import { useGetAllBrandsQuery } from "@states/product/brand.api";
import { TBrand } from "@type-defs/product/TBrand";
import { useGetAllProductTagsQuery } from "@states/product/product-tag.api";
import { TProductTag } from "@type-defs/product/TProductTag";
import { useGetAllUnitOfMeasurementsQuery } from "@states/unit/unit-of-measurement.api";
import { TUnit } from "@type-defs/unit/TUnit";
import { removeKeys } from "@utils/general/removeKeys";
import {
  useCreateProductMutation,
  useGetProductQuery,
  useUpdateProductMutation,
} from "@states/product/product.api";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { castTypeArr } from "@utils/general/cast-type";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { Toast } from "@helpers/popups/Toast";
import { useNavigate, useParams } from "react-router-dom";
import { PRODUCT } from "@utils/constants/product.constants";
import { getBaseUrl } from "@utils/general/getBaseUrl";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import SamePriceCheck from "./SamePriceCheck";
import { formatDBDate } from "@utils/general/format-db-date";
import {
  useGetAllBaseUnitListQuery,
  useGetAllProductPriceHistoryQuery,
  useGetAllProductTypesQuery,
  useGetAllSKUQuery,
  useLazyGetAllUnitTypeByBaseUnitQuery,
} from "@states/common/common.api";
import { useSelector } from "react-redux";
import { RootState } from "@states/store";
import { LOCAL_STORAGE_KEYS } from "@utils/constants/local-storeage-keys.constants";
import PriceHistoryModal from "@components/modals/product/PriceHistoryModal";
import AuthComponent from "@components/auth/component";
import { PRODUCT_PRICE } from "@utils/constants/product-price.constants";

const ProductPriceEditPage = () => {
  const { id } = useParams();
  const [sameWithSecondary, setSameWithSecondary] = useState(false);

  const [primarySalesPrice, setPrimarySalesprice] = useState(undefined);
  const [primarySalesTax, setPrimarySalesTax] = useState(undefined);
  const [primarySalesTaxType, setPrimarySalesTaxType] = useState(undefined);

   const [secondarySalesPrice, setSecondarySalesprice] = useState(undefined);
  const [secondarySalesTax, setSecondarySalesTax] = useState(undefined);
  const [secondarySalesTaxType, setSecondarySalesTaxType] = useState(undefined);

   const [directSalesPrice, setDirectSalesprice] = useState(undefined);
  const [directSalesTax, setDirectSalesTax] = useState(undefined);
  const [directSalesTaxType, setDirectSalesTaxType] = useState(undefined);


  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const { formValues } = useSelector((state: RootState) => state.common);
  const navigate = useNavigate();
  const [createLoading, setCreateLoading] = useState(false);

  const { data: productData } = useGetProductQuery({ id: id! });
  const { data: priceHistory, refetch } = useGetAllProductPriceHistoryQuery({
    productId: id!,
  });
  const [
    updateProduct,
    { isLoading, error: updateError, isSuccess, originalArgs },
  ] = useUpdateProductMutation();
  const { data: productCategoryData, isLoading: productCategoryLoading } =
    useGetAllProductCategoriesQuery({ limit: 1000 });
  const productCategoryReponse =
    productCategoryData as any as TSuccessResponse<TProductCategory>;
  const { data: brandData, isLoading: brandLoading } = useGetAllBrandsQuery({});
  const brandResponse = brandData as any as TSuccessResponse<TBrand>;
  const { data: productTagData, isLoading: productTagLoading } =
    useGetAllProductTagsQuery({ limit: 1000 });
  const productTagResponse =
    productTagData as any as TSuccessResponse<TProductTag>;
  const { data: prodcutTypeData, isLoading: productTypeLoading } =
    useGetAllProductTypesQuery();
  const { data: skuData, isLoading: skuDataLoading } = useGetAllSKUQuery();
  const { data: baseUnitData, isLoading: unitLoading } =
    useGetAllBaseUnitListQuery();
  const [
    getAllUnitTypeByBaseUnit,
    {
      data: unitTypeData,
      isLoading: unitTypeLoading,
      error: getAllUnitTypeByBaseUnitError,
    },
  ] = useLazyGetAllUnitTypeByBaseUnitQuery();
  useErrorHandling(
    ...castTypeArr<TFailResponse>([updateError, getAllUnitTypeByBaseUnitError])
  );

  const fields = useMemo(() => {
    return productPriceFields({
      
      sameWithSecondary,
      secondarySalesPriceValue: secondarySalesPrice,
      secondarySalesTaxValue: secondarySalesTax,
      secondarySalesTaxTypeValue: secondarySalesTaxType,

      primarySalesPriceValue: primarySalesPrice,
      primarySalesTaxValue: primarySalesTax,
      primarySalesTaxTypeValue: primarySalesTaxType,

      directSalesPriceValue: directSalesPrice,
      directSalesTaxValue: directSalesTax,
      directSalesTaxTypeValue: directSalesTaxType,

     
      withHistory: {
        priceHistory: true,
      },
      setShowHistoryModal: setIsHistoryOpen,
    
    });
  }, [
    formValues,
    sameWithSecondary,
    secondarySalesPrice,
    secondarySalesTax,
    secondarySalesTaxType,
    directSalesTaxType,
    directSalesPrice,
    directSalesTax,
    primarySalesPrice,
    primarySalesTax,
    primarySalesTaxType
  ]);
  const getValuesByParent = (getValues: any, setValue: any) => {
    const primarySalesPrice = getValues("primary_sales_price");
    const primarySalesTax = getValues("primary_sales_tax");
    const primarySalesTaxType = getValues("primary_sales_tax_type");


    if (sameWithSecondary) {
      setSecondarySalesprice(primarySalesPrice);
      setSecondarySalesTax(primarySalesTax);
      setSecondarySalesTaxType(primarySalesTaxType);
      
      setValue("direct_sales_price", primarySalesPrice);
      setValue("direct_sales_tax", primarySalesTax);
      setValue("direct_sales_tax_type", primarySalesTaxType);

      setValue("secondary_sales_tax_type", primarySalesTaxType);
      setValue("secondary_sales_price", primarySalesPrice);
      setValue("secondary_sales_tax", primarySalesTax);


      setPrimarySalesprice(primarySalesPrice);
      setPrimarySalesTax(primarySalesTax);
      setPrimarySalesTaxType(primarySalesTaxType);
    }
  };

  useEffect(() => {
    const {
      primary_sales_price,
      primary_sales_tax,
      primary_sales_tax_type,
      unit_id,
    } = formValues;
    if (unit_id) {
      getAllUnitTypeByBaseUnit({ unit_id });
    }
    if (primary_sales_price) {
      setSecondarySalesprice(primary_sales_price);
      setSecondarySalesTax(primary_sales_tax);
      setSecondarySalesTaxType(primary_sales_tax_type);
      setDirectSalesTaxType(primary_sales_tax_type);
      setDirectSalesprice(primary_sales_price);
      setDirectSalesTax(primary_sales_tax);
      setDirectSalesTaxType(primary_sales_tax_type);

      setPrimarySalesprice(primary_sales_price);
      setPrimarySalesTax(primary_sales_tax);
      setPrimarySalesTaxType(primary_sales_tax_type);
    }
  }, [formValues, sameWithSecondary]);
  useErrorHandling(...castTypeArr<TFailResponse>([updateError]));

  const asyncDispatcher = useCallback(
    async (reqBody: any) => {
      try {
        const parsedBody = removeKeys(reqBody, "remark");
        const {
          product_photo,
          manufacture_date,
          expire_date,
          // primary_sales_price,
          // primary_sales_tax,
          // primary_sales_tax_type,
          ...theRest
        } = parsedBody;
        const formData = new FormData();

        if (product_photo && typeof product_photo !== "string") {
          formData.append("product_photo", product_photo[0]);
        }

        for (const [key, value] of Object.entries(theRest)) {
          //@ts-ignore
          formData.append(key, value);
        }
        console.log("formData",formData);
        const response = await fetch(`${getBaseUrl()}product/price-update/${id}`, {
          method: "PATCH",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              LOCAL_STORAGE_KEYS.ACCESS_TOKEN_KEY
            )}`, // Add token here
          },
        });

        const res = await response.json();
        if (res.status === "fail") {
          Toast.fire({
            title: res.message,
            icon: "error",
          });
        }
        if (res.status === "error" && res.errorCode === 500  ) {
          Toast.fire({
            title: res.message,
            icon: "error",
          });

          return;
        }

        if (res?.errors?.length > 0) {
          for (const error of res.errors) {
            Toast.fire({
              title: error,
              icon: "error",
            });
          }

          return;
        }

        const { success, message } = res as TSuccessResponse;

        if (success || isSuccess) {
          Toast.fire({
            title: message,
            icon: "success",
          });

          navigate(PRODUCT_PRICE.BASE_PATH, {
            state: {
              reload: true,
            },
          });
        }
      } catch (error) {
        console.log("Error", error);
      }
    },
    [updateProduct, isSuccess, Toast, navigate]
  );

  return (
    <AuthComponent
      action="edit"
      module="Product"
      subModule="Product Price"
      page
    >
      <PageWrapper>
        {(isLoading || createLoading) && <LoadingSpinner />}
        <PriceHistoryModal
          isOpen={isHistoryOpen}
          setIsOpen={setIsHistoryOpen}
          data={priceHistory}
        />
        <CustomForm
          title="Edit Product Price"
          schema={productPriceSchema}
          fields={fields}
          asyncDispatcher={asyncDispatcher}
          getValuesByParent={getValuesByParent}
          getValuesNow={sameWithSecondary}
          //@ts-ignore
          defaultValues={productData?.data}
          customComponent={
            <SamePriceCheck setSameWithSecondary={setSameWithSecondary} />
          }
        />
      </PageWrapper>
    </AuthComponent>
  );
};

export default ProductPriceEditPage;
